import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, CircularProgress, Alert, Divider, List, ListItem,
  ListItemText, ListItemAvatar, Avatar, Chip, Accordion, AccordionSummary,
  AccordionDetails, Button, Tooltip, FormControlLabel, Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ModalWrapper from '../components/ModalWrapper';
import ShoppingListFormModal from './ShoppingListFormModal';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { ShoppingList } from '../types/ShoppingList';
import { Price } from '../types/Price';
import { Store } from '../types/Store';
import { apiFetch } from '../apiFetch';

interface AnalyzeListModalProps {
  open: boolean;
  onClose: () => void;
  groceries: ShoppingListGrocery[];
  listName: string;
}

interface PriceWithStore {
  price: Price;
  store: Store;
  effectivePrice: number;  // The price value to use for comparison
}

interface GroceryWithPrice {
  item: ShoppingListGrocery;
  price: Price;
  store: Store;
  effectivePrice: number;  // The price value being used
  otherPrices: PriceWithStore[];
  isOnSale: boolean;
  mayBeOnSale?: { store: Store; salePrice: string };  // When not including sales
}

interface OnSaleGrocery extends GroceryWithPrice {
  daysOnSale: number;
}

interface StoreGroup {
  store: Store;
  items: GroceryWithPrice[];
  totalPrice: number;
}

// Get initials from store name (e.g., "Cedar General Store" -> "CGS")
const getStoreInitials = (name: string): string => {
  return name
    .split(/\s+/)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);
};

const AnalyzeListModal: React.FC<AnalyzeListModalProps> = ({
  open,
  onClose,
  groceries,
  listName,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeSales, setIncludeSales] = useState(true);

  // Raw data from API
  const [pricesData, setPricesData] = useState<Price[]>([]);
  const [storeMap, setStoreMap] = useState<Map<number, Store>>(new Map());

  // Create list modal state
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [selectedStoreGroup, setSelectedStoreGroup] = useState<StoreGroup | null>(null);
  const [existingListNames, setExistingListNames] = useState<string[]>([]);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch data when modal opens
  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [prices, stores, lists] = await Promise.all([
          apiFetch(`${apiUrl}/api/prices/`),
          apiFetch(`${apiUrl}/api/shops/`),
          apiFetch(`${apiUrl}/api/shopping-lists/`),
        ]);

        const storeMapping = new Map<number, Store>();
        stores.forEach((store: Store) => {
          storeMapping.set(store.id, store);
        });

        setPricesData(prices);
        setStoreMap(storeMapping);
        setExistingListNames(lists.map((l: ShoppingList) => l.name.toLowerCase()));
      } catch (err: any) {
        setError(err.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, apiUrl]);

  // Analyze data based on includeSales setting
  const { unpricedItems, onSaleItems, storeGroups } = useMemo(() => {
    if (loading || pricesData.length === 0) {
      return { unpricedItems: [], onSaleItems: [], storeGroups: [] };
    }

    // Group all prices by grocery ID, then by store ID (keeping all prices ordered by date)
    const pricesByGroceryAndStore = new Map<number, Map<number, Price[]>>();
    pricesData.forEach((price: Price) => {
      if (!pricesByGroceryAndStore.has(price.grocery)) {
        pricesByGroceryAndStore.set(price.grocery, new Map());
      }
      const storesPrices = pricesByGroceryAndStore.get(price.grocery)!;
      if (!storesPrices.has(price.shop)) {
        storesPrices.set(price.shop, []);
      }
      storesPrices.get(price.shop)!.push(price);
    });

    // Filter out deleted groceries
    const activeGroceries = groceries.filter(item => !item.grocery_details.deleted);

    const unpriced: ShoppingListGrocery[] = [];
    const onSale: OnSaleGrocery[] = [];
    const pricedItems: GroceryWithPrice[] = [];

    for (const item of activeGroceries) {
      const groceryStorePrices = pricesByGroceryAndStore.get(item.grocery);

      if (!groceryStorePrices || groceryStorePrices.size === 0) {
        unpriced.push(item);
        continue;
      }

      // For each store, determine the effective price to use
      const storePriceOptions: Array<{
        store: Store;
        price: Price;
        effectivePrice: number;
        isCurrentlySale: boolean;
        salePrice?: string;
      }> = [];

      groceryStorePrices.forEach((prices, storeId) => {
        const store = storeMap.get(storeId);
        if (!store) return;

        // Prices are ordered by -created_at, so first is most recent
        const latestPrice = prices[0];

        if (includeSales) {
          // Use sale prices as-is
          storePriceOptions.push({
            store,
            price: latestPrice,
            effectivePrice: parseFloat(latestPrice.price),
            isCurrentlySale: latestPrice.is_discounted,
          });
        } else {
          // Don't include sales - use normal price instead
          if (latestPrice.is_discounted) {
            // Try to use price_before_discount
            if (latestPrice.price_before_discount) {
              storePriceOptions.push({
                store,
                price: latestPrice,
                effectivePrice: parseFloat(latestPrice.price_before_discount),
                isCurrentlySale: true,
                salePrice: latestPrice.price,
              });
            } else {
              // Find previous non-sale price at this store
              const previousNonSale = prices.find(p => !p.is_discounted);
              if (previousNonSale) {
                storePriceOptions.push({
                  store,
                  price: previousNonSale,
                  effectivePrice: parseFloat(previousNonSale.price),
                  isCurrentlySale: true,
                  salePrice: latestPrice.price,
                });
              }
              // If no previous non-sale price, skip this store for this item
            }
          } else {
            // Not on sale, use as-is
            storePriceOptions.push({
              store,
              price: latestPrice,
              effectivePrice: parseFloat(latestPrice.price),
              isCurrentlySale: false,
            });
          }
        }
      });

      if (storePriceOptions.length === 0) {
        // No valid prices (all were sales without fallback)
        unpriced.push(item);
        continue;
      }

      // Find cheapest option
      storePriceOptions.sort((a, b) => a.effectivePrice - b.effectivePrice);
      const cheapest = storePriceOptions[0];

      // Build other prices list
      const otherPrices: PriceWithStore[] = storePriceOptions
        .slice(1)
        .map(opt => ({
          price: opt.price,
          store: opt.store,
          effectivePrice: opt.effectivePrice,
        }));

      const groceryWithPrice: GroceryWithPrice = {
        item,
        price: cheapest.price,
        store: cheapest.store,
        effectivePrice: cheapest.effectivePrice,
        otherPrices,
        isOnSale: includeSales && cheapest.price.is_discounted,
        mayBeOnSale: !includeSales && cheapest.isCurrentlySale
          ? { store: cheapest.store, salePrice: cheapest.salePrice! }
          : undefined,
      };

      pricedItems.push(groceryWithPrice);

      // Track on-sale items (only when includeSales is true)
      if (includeSales && cheapest.price.is_discounted) {
        const createdAt = cheapest.price.created_at ? new Date(cheapest.price.created_at) : new Date();
        const now = new Date();
        const daysOnSale = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

        onSale.push({
          ...groceryWithPrice,
          daysOnSale,
        });
      }
    }

    // Group priced items by store
    const groupsByStore = new Map<number, GroceryWithPrice[]>();
    pricedItems.forEach((priced) => {
      const existing = groupsByStore.get(priced.store.id) || [];
      existing.push(priced);
      groupsByStore.set(priced.store.id, existing);
    });

    // Convert to array and calculate totals
    const groups: StoreGroup[] = [];
    groupsByStore.forEach((items, storeId) => {
      const store = storeMap.get(storeId);
      if (store) {
        const totalPrice = items.reduce((sum, item) => {
          return sum + item.effectivePrice * item.item.quantity;
        }, 0);
        groups.push({ store, items, totalPrice });
      }
    });

    // Sort groups by total price
    groups.sort((a, b) => a.totalPrice - b.totalPrice);

    // Sort on-sale items by price
    onSale.sort((a, b) => a.effectivePrice - b.effectivePrice);

    return { unpricedItems: unpriced, onSaleItems: onSale, storeGroups: groups };
  }, [loading, pricesData, storeMap, groceries, includeSales]);

  const formatDaysOnSale = (days: number): string => {
    if (days === 0) return 'New today';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  const handleCreateListFromGroup = (group: StoreGroup) => {
    setSelectedStoreGroup(group);
    setCreateListModalOpen(true);
  };

  const handleListCreated = (newList: ShoppingList) => {
    setCreateListModalOpen(false);
    setSelectedStoreGroup(null);
    onClose();
    navigate(`/shopping-lists/${newList.id}`);
  };

  const getPrefillName = (): string => {
    if (!selectedStoreGroup) return '';
    const storeInitials = getStoreInitials(selectedStoreGroup.store.name);
    let baseName = `${listName} cheapest @ ${storeInitials}`;

    // Check if name already exists and add suffix if needed
    if (!existingListNames.includes(baseName.toLowerCase())) {
      return baseName;
    }

    // Find the next available number
    let counter = 2;
    while (existingListNames.includes(`${baseName} (${counter})`.toLowerCase())) {
      counter++;
    }
    return `${baseName} (${counter})`;
  };

  const getPrefillDescription = (): string => {
    if (!selectedStoreGroup) return '';
    return `Created from "${listName}" analysis. Contains ${selectedStoreGroup.items.length} item${selectedStoreGroup.items.length !== 1 ? 's' : ''} that are cheapest at ${selectedStoreGroup.store.name}.`;
  };

  const getPrefillItems = (): Array<{ grocery: number; quantity: number }> => {
    if (!selectedStoreGroup) return [];
    return selectedStoreGroup.items.map(item => ({
      grocery: item.item.grocery,
      quantity: item.item.quantity,
    }));
  };

  const renderPriceComparison = (otherPrices: PriceWithStore[]) => {
    if (otherPrices.length === 0) return null;

    return (
      <Typography variant="caption" color="text.secondary" component="span">
        {' '}compared to:{' '}
        {otherPrices.map((op, idx) => (
          <span key={op.store.id}>
            {idx > 0 && ', '}
            ${op.effectivePrice.toFixed(2)}
            {' @ '}
            <Tooltip title={op.store.name} arrow>
              <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                {getStoreInitials(op.store.name)}
              </span>
            </Tooltip>
          </span>
        ))}
      </Typography>
    );
  };

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        Analyze: {listName}
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Store Groups Section */}
          {storeGroups.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorefrontIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    By Cheapest Store
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeSales}
                      onChange={(e) => setIncludeSales(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Include Sales</Typography>}
                  sx={{ mr: 0 }}
                />
              </Box>
              {storeGroups.map((group) => (
                <Accordion key={group.store.id} defaultExpanded={storeGroups.length === 1}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                      <Avatar
                        src={group.store.icon_url || undefined}
                        sx={{ width: 28, height: 28 }}
                      >
                        <StorefrontIcon fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {group.store.name}
                      </Typography>
                      <Chip
                        label={`${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="body2" color="primary" fontWeight="medium">
                        ${group.totalPrice.toFixed(2)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List dense disablePadding>
                      {group.items.map((priced) => (
                        <ListItem key={priced.item.id} disablePadding sx={{ py: 0.5 }}>
                          <ListItemAvatar sx={{ minWidth: 40 }}>
                            <Avatar
                              src={priced.item.grocery_details.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
                              variant="rounded"
                              sx={{ width: 28, height: 28 }}
                            />
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2">
                                  {priced.item.quantity}x {priced.item.grocery_details.name}
                                </Typography>
                                {priced.isOnSale && (
                                  <Chip
                                    icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                                    label="Sale"
                                    size="small"
                                    color="error"
                                    sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
                                  />
                                )}
                                {priced.mayBeOnSale && (
                                  <Tooltip title={`May be on sale at ${priced.mayBeOnSale.store.name}`} arrow>
                                    <Chip
                                      icon={<LocalOfferIcon sx={{ fontSize: 12 }} />}
                                      label={`$${priced.mayBeOnSale.salePrice}? @ ${getStoreInitials(priced.mayBeOnSale.store.name)}`}
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 }, cursor: 'help' }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box component="span">
                                <Typography variant="caption" color="text.secondary" component="span">
                                  ${priced.effectivePrice.toFixed(2)} each = ${(priced.effectivePrice * priced.item.quantity).toFixed(2)}
                                </Typography>
                                {renderPriceComparison(priced.otherPrices)}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlaylistAddIcon />}
                        onClick={() => handleCreateListFromGroup(group)}
                      >
                        Create List from {getStoreInitials(group.store.name)} Items
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}

          {/* On Sale Section - only show when includeSales is true */}
          {includeSales && onSaleItems.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOfferIcon color="error" />
                    <Typography variant="subtitle1" fontWeight="medium">
                      On Sale ({onSaleItems.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    These items were discounted the last time their price was recorded and may still be on sale.
                  </Typography>
                  <List dense disablePadding>
                    {onSaleItems.map((sale) => (
                      <ListItem key={sale.item.id} disablePadding sx={{ py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar
                            src={sale.item.grocery_details.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
                            variant="rounded"
                            sx={{ width: 32, height: 32 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2">
                                {sale.item.quantity}x {sale.item.grocery_details.name}
                              </Typography>
                              <Chip
                                label={`$${sale.price.price}`}
                                size="small"
                                color="error"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                              {sale.price.price_before_discount && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textDecoration: 'line-through' }}
                                >
                                  ${sale.price.price_before_discount}
                                </Typography>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {sale.store.name} - {formatDaysOnSale(sale.daysOnSale)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Unpriced Section */}
          {unpricedItems.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpOutlineIcon color="disabled" />
                    <Typography variant="subtitle1" fontWeight="medium" color="text.secondary">
                      No Price Data ({unpricedItems.length})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {includeSales
                      ? "These items don't have any recorded prices yet."
                      : "These items don't have any non-sale prices recorded."}
                  </Typography>
                  <List dense disablePadding>
                    {unpricedItems.map((item) => (
                      <ListItem key={item.id} disablePadding sx={{ py: 0.25 }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar
                            src={item.grocery_details.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
                            variant="rounded"
                            sx={{ width: 28, height: 28 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {item.quantity}x {item.grocery_details.name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </>
          )}

          {/* Empty state */}
          {onSaleItems.length === 0 && storeGroups.length === 0 && unpricedItems.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No groceries to analyze in this list.
              </Typography>
            </Box>
          )}
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </Box>

      {/* Create List Modal */}
      <ShoppingListFormModal
        open={createListModalOpen}
        onClose={() => {
          setCreateListModalOpen(false);
          setSelectedStoreGroup(null);
        }}
        onSuccess={handleListCreated}
        prefillName={getPrefillName()}
        prefillDescription={getPrefillDescription()}
        prefillItems={getPrefillItems()}
      />
    </ModalWrapper>
  );
};

export default AnalyzeListModal;
